import React, { useState } from 'react';
import { Button, Card, CardHeader, CardTitle, CardContent } from '@/components/ui';
import { useLineItems, useCreateLineItem, useUpdateLineItem, useDeleteLineItem, useBulkUpdateLineItems } from '@/hooks/useHoldRequests';
import { useUpdateHoldPosition } from '@/hooks/useCharting';
import { useAuthContext } from '@/hooks/useAuthContext';
import LineItemForm from './LineItemForm';
import LineItemList from './LineItemList';
import type { CreateLineItemData, UpdateLineItemData } from '@/types';

interface LineItemManagementProps {
  holdRequestId: string;
  businessCategory?: string | undefined;
  readOnlyHoldPosition?: boolean; // New prop to make hold position read-only
}

export default function LineItemManagement({ holdRequestId, businessCategory, readOnlyHoldPosition = false }: LineItemManagementProps) {
  const [showAddForm, setShowAddForm] = useState(false);
  const { user } = useAuthContext();

  // Covers update, delete, and bulk-update failures
  const [mutationError, setMutationError] = useState<string | null>(null);

  // Fetch line items
  const { data: lineItems = [], isLoading: isLoadingItems } = useLineItems(holdRequestId);

  // Mutations
  const createMutation = useCreateLineItem();
  const updateMutation = useUpdateLineItem();
  const deleteMutation = useDeleteLineItem();
  const bulkUpdateMutation = useBulkUpdateLineItems();
  const positionMutation = useUpdateHoldPosition(); // OPE-382: positions go via set-position

  // Check if user can edit line items (only charting roles and leadership)
  const canEdit = !!(user && (
    user.role === 'charting_member' ||
    user.role === 'charting_manager' ||
    user.role === 'sales_vp' ||
    user.role === 'gvp' ||
    user.role === 'rvp' ||
    user.role === 'adops_leadership'
  ));

  const handleCreate = async (data: CreateLineItemData) => {
    try {
      await createMutation.mutateAsync({
        holdRequestId,
        ...data,
      });
      setShowAddForm(false);
    } catch (error) {
      console.error('Failed to create line item:', error);
      // Error handling would show a toast notification in production
    }
  };

  const handleUpdate = async (id: string, data: UpdateLineItemData) => {
    setMutationError(null);

    // OPE-382: `holdPosition` is no longer writable through PUT /items/:id — that path
    // enforced none of the queue invariants (it is how a unit reached six simultaneous
    // primaries). Split it out to `set-position`, which enforces the depth cap and the
    // single-primary rule, and accepts `null` to clear. Both LineItemRow position
    // controls (the read-mode dropdown and the edit-mode row) reach this one handler.
    //
    // Ordinary fields go FIRST so the position is validated against the item's NEW
    // flight dates — the overlap window is what the cap is computed over, so ordering
    // it the other way could wave through a genuine cap violation.
    //
    // The trade-off is a possible partial save when only the position leg fails. Left
    // as-is deliberately: there is no transaction across two endpoints, both other
    // orderings are worse (validating the cap against stale dates, or a pre-check that
    // just moves the race), nothing is corrupted, and a retry re-sends the same field
    // values and converges. What the banner must not do is stay silent about the half
    // that DID save — see the catch below.
    const { holdPosition, ...rest } = data;
    try {
      if (Object.keys(rest).length > 0) {
        await updateMutation.mutateAsync({ id, data: rest });
      }
      if (holdPosition !== undefined) {
        try {
          await positionMutation.mutateAsync({ lineItemId: id, holdPosition });
        } catch (positionError) {
          // Two endpoints, no shared transaction — so if the ordinary fields already
          // committed, say so explicitly. Silence here reads as "nothing saved" and
          // sends the chartist looking for changes that are in fact persisted.
          // Retrying is safe: the field write re-sends the same values.
          const msg = (positionError as { message?: string })?.message;
          const savedRest = Object.keys(rest).length > 0;
          const prefix = savedRest
            ? 'Your other changes were saved, but the position was not changed'
            : 'Position not changed';
          setMutationError(msg ? `${prefix}: ${msg}` : `${prefix}.`);
          throw positionError;
        }
      }
    } catch (error) {
      console.error('Failed to update line item:', error);
      setMutationError((prev) => {
        if (prev) return prev; // keep the more specific position message
        const msg = (error as { message?: string })?.message;
        return msg || 'Failed to save changes. Please try again.';
      });
      throw error; // re-throw so LineItemList keeps the row in edit mode for retry
    }
  };

  const handleDelete = async (id: string) => {
    // Clear any stale banner before prompting — avoids confusing leftover messages
    setMutationError(null);
    if (!window.confirm('Are you sure you want to delete this line item?')) {
      return;
    }
    try {
      await deleteMutation.mutateAsync({ id, holdRequestId });
    } catch (error) {
      console.error('Failed to delete line item:', error);
      const msg = (error as { message?: string })?.message;
      setMutationError(msg || 'Failed to delete line item. Please try again.');
    }
  };

  const handleBulkUpdate = async (lineItemIds: string[], updates: UpdateLineItemData) => {
    setMutationError(null);
    try {
      const result = await bulkUpdateMutation.mutateAsync({
        lineItemIds,
        updates,
      });
      if (result.summary.failed > 0) {
        throw new Error(`${result.summary.failed} item(s) could not be updated.`);
      }
    } catch (error) {
      console.error('Failed to bulk update line items:', error);
      const msg = (error as { message?: string })?.message;
      setMutationError(msg || 'Failed to update items. Please try again.');
      throw error; // re-throw so LineItemList keeps the selection open for retry
    }
  };

  // OPE-382: positionMutation counts too. Without it the row's position dropdown stays
  // enabled while the set-position POST is in flight, so a second change can start
  // before the first responds — two writes racing for the same queue slot.
  const isLoading = createMutation.isPending || updateMutation.isPending ||
                    deleteMutation.isPending || bulkUpdateMutation.isPending ||
                    positionMutation.isPending;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Inventory Line Items</CardTitle>
          {canEdit && (
            <Button
              variant="primary"
              onClick={() => setShowAddForm(!showAddForm)}
              disabled={isLoading}
            >
              {showAddForm ? 'Cancel' : 'Add Line Item'}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {mutationError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md" role="alert">
            <p className="text-sm text-red-700">{mutationError}</p>
          </div>
        )}
        {!canEdit && lineItems.length > 0 && (
          <div className="mb-4 p-3 bg-outfront-purple-light border border-outfront-purple rounded-md">
            <p className="text-sm text-outfront-purple">
              <strong>View Only:</strong> Line items can only be edited by the charting team after creation.
            </p>
          </div>
        )}

        {showAddForm && canEdit && (
          <div className="mb-6 p-4 bg-neutral-50 rounded-lg">
            <h4 className="text-sm font-semibold text-neutral-900 mb-4">Add New Line Item</h4>
            <LineItemForm
              onSubmit={handleCreate}
              onCancel={() => setShowAddForm(false)}
              isLoading={createMutation.isPending}
              businessCategory={businessCategory}
            />
          </div>
        )}

        {isLoadingItems ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <LineItemList
            lineItems={lineItems}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onBulkUpdate={handleBulkUpdate}
            isLoading={isLoading}
            businessCategory={businessCategory}
            canEdit={canEdit}
            readOnlyHoldPosition={readOnlyHoldPosition}
          />
        )}
      </CardContent>
    </Card>
  );
}
